import React, {
	ReactNode,
	useContext,
	useMemo,
	useRef,
	useCallback,
} from 'react'
import {
	ColumnDef,
	useReactTable,
	getCoreRowModel,
	Row,
	AccessorKeyColumnDef,
	Column,
} from '@tanstack/react-table'
import {
	Circle,
	Delete,
	Drafts,
	FilterAltOff,
	KeyboardArrowDown,
	KeyboardArrowUp,
	Markunread,
	UnfoldMore,
	Flag,
	FlagOutlined,
} from '@mui/icons-material'
import { ReactComponent as ResetColumnsSize } from '../../assets/icons/table/reset-columns.svg'
import { useUserDateFormat } from '../useUserDateFormat'
import {
	IconButton,
	Checkbox,
	EllipsisText,
	Tooltip,
} from '../../components/ui'
import {
	InboxItem,
	InboxNotificationCategory,
} from '../../lib/api/types/inbox.interfaces'
import cn from '../../lib/utils/cn'
import AppContext from '../../context/appContext'
import {
	CustomColumnFilter,
	OrderByType,
	OrderDirectionType,
} from '../../pages/Workspace/components/interfaces.table'
import FilterButton from '../../components/table/FilterButton'
import { mapInboxMessage } from '../../lib/utils/mappers/inboxMapper'
import { useToggleInboxPinned, useToggleInboxRead } from '../api/inboxHooks'

interface UseCreateInboxTableProps {
	data?: InboxItem[]
	checkedItems: { id: string }[]
	onToggleItem: (id: string, checked: boolean) => void
	onToggleAll: (ids: string[], checked: boolean) => void
	onDeleteItem: (id: string) => void
	onChangeFilters: (newFilters: CustomColumnFilter[]) => void
	onRefetch?: () => void
	columnFilters: CustomColumnFilter[]
	sortControllers?: {
		orderBy: OrderByType
		orderDirection: OrderDirectionType
		toggleSorting: (columnId: OrderByType) => void
	}
	isMobile?: boolean
	isWide?: boolean
}

type InboxTableEntry = {
	id: string
	name: string
	itemStatus: boolean
	itemCategory: InboxNotificationCategory
	itemPinned: boolean
	from: string
	createdAt: string
	options: {
		id: string
	}
	messageIcon: ReactNode
	rawTemplate: string
	objects: any[]
}

export const useCreateInboxTable = ({
	data = [],
	checkedItems,
	onToggleItem,
	onToggleAll,
	onDeleteItem,
	onChangeFilters,
	onRefetch = () => { },
	columnFilters,
	sortControllers,
	isMobile,
	isWide,
}: UseCreateInboxTableProps) => {
	const { isSidebarOpen } = useContext(AppContext)
	const { formatDateTime } = useUserDateFormat()

	const loadingReadItems = useRef<Set<string>>(new Set())
	const loadingFavoriteItems = useRef<Set<string>>(new Set())

	const { mutateAsync: toggleReadStatus, isPending: isTogglingRead } =
		useToggleInboxRead()
	const { mutateAsync: togglePinnedStatus, isPending: isTogglingPinned } =
		useToggleInboxPinned()

	const maxAvailableNameSize = useMemo(
		() =>
			Math.min(
				Math.floor(window.innerWidth * (isSidebarOpen ? 0.5 : 0.6)),
				400
			),
		[isSidebarOpen]
	)

	const renderSortingHeader = (
		title: string,
		column: Column<InboxTableEntry, unknown>,
		className?: string,
		leftIcon?: ReactNode
	) => {
		const multiInputLabel = () => {
			switch (column?.id) {
				case 'name':
					return 'Category'
				case 'from': {
					return 'From'
				}
				default:
					return ''
			}
		}

		return (
			<div
				className={cn(
					'w-full flex group justify-start items-center gap-1 cursor-pointer select-none h-full group box-border',
					className
				)}
				onClick={() => {
					sortControllers?.toggleSorting(column?.id as OrderByType)
				}}
			>
				<FilterButton
					columnKey={column?.id === 'name' ? 'category' : column?.id}
					setColumnFilters={onChangeFilters}
					columnFilters={columnFilters}
					isDate={['createdAt']?.includes(column?.id)}
					buttonConfig={{
						tooltip: column?.id === 'name' ? 'Filter by category' : null,
					}}
					multiValueItems={
						column?.id === 'name'
							? [
								{ id: InboxNotificationCategory.File, label: 'File' },
								{ id: InboxNotificationCategory.Event, label: 'Event' },
								{ id: InboxNotificationCategory.Task, label: 'Task' },
								{ id: InboxNotificationCategory.Access, label: 'Access' },
								{ id: InboxNotificationCategory.Team, label: 'Team' },
								{ id: InboxNotificationCategory.Security, label: 'Security' },
							]
							: null
					}
					extraMultiValueItems={
						column?.id === 'from'
							? [{ id: 'system', label: 'System Notifications' }]
							: null
					}
					inputLabel={multiInputLabel()}
					inputLabelClassname="capitalize text-sm text-text-table dark:text-text-table-dark text-left"
					objectType={column?.id === 'from' ? 'user' : null}
				/>

				<EllipsisText
					tooltipLabel={title}
					text={title}
					leftIcon={leftIcon}
					className="text-center w-fit max-w-auto text-text-table dark:text-text-table-dark"
				/>

				{sortControllers && (
					<span
						className={cn(
							'flex items-center justify-center transition-all duration-200 w-3 h-5',
							column?.id !== sortControllers.orderBy &&
							'hidden group-hover:inline-block'
						)}
					>
						{column?.id === sortControllers.orderBy &&
							sortControllers?.orderDirection === 'asc' && (
								<KeyboardArrowUp className="w-5 h-5" />
							)}
						{column?.id === sortControllers.orderBy &&
							sortControllers?.orderDirection === 'desc' && (
								<KeyboardArrowDown className="w-5 h-5" />
							)}
						{column?.id !== sortControllers.orderBy && (
							<UnfoldMore className="h-5 w-5 opacity-0 group-hover:opacity-100 duration-default -ml-1" />
						)}
					</span>
				)}
			</div>
		)
	}

	const tableData = useMemo<InboxTableEntry[]>(() => {
		return (data || []).map((entry) => {
			const { message, icon } = mapInboxMessage(entry)
			return {
				id: entry?.id,
				name: message,
				itemStatus: entry?.isRead,
				itemCategory: entry?.category,
				itemPinned: entry?.isPinned,
				from: entry.from?.name || 'System',
				createdAt:
					typeof entry.createdAt === 'string'
						? entry.createdAt
						: entry.createdAt instanceof Date
							? entry.createdAt.toISOString()
							: String(entry.createdAt),
				options: {
					id: entry?.id,
				},
				messageIcon: icon,
				rawTemplate: entry.template,
				objects: entry.objects,
			}
		})
	}, [data])

	const columns = useMemo<ColumnDef<InboxTableEntry, unknown>[]>(() => {
		const base: ColumnDef<InboxTableEntry>[] = [
			{
				accessorKey: 'name',
				enableResizing: true,
				minSize: 200,
				size: isMobile
					? Math.floor(window.innerWidth * 0.85)
					: isWide
						? maxAvailableNameSize
						: 300,
				header: ({ column }: { column: Column<InboxTableEntry, unknown> }) => {
					const rows = table.getRowModel().rows
					const allIds = rows.map(
						(row: Row<InboxTableEntry>) => row.original.id
					)

					const isAllSelected = Boolean(
						allIds?.length &&
						allIds?.every((id) =>
							checkedItems?.find((item) => item?.id === id)
						)
					)
					return (
						<div className="h-full flex items-center gap-6 max-w-full pl-3 md:pl-6 group cursor-pointer">
							{!isMobile && (
								<Checkbox
									checked={isAllSelected}
									disabled={!allIds?.length}
									onChange={() => onToggleAll?.(allIds, !isAllSelected)}
								/>
							)}

							{renderSortingHeader('Title', column)}
						</div>
					)
				},
				cell: (info) => {
					const isChecked = checkedItems.some(
						(item) => item?.id === info?.row.original.id
					)
					const status = info?.row?.original?.itemStatus
					const isPinned = info?.row?.original?.itemPinned
					const messageIcon = info?.row?.original?.messageIcon

					return (
						<div className="flex items-center gap-6 pl-6">
							{!isMobile && (
								<Checkbox
									checked={isChecked}
									onChange={() =>
										onToggleItem(info?.row.original.id, !isChecked)
									}
								/>
							)}

							<div className="flex items-center justify-normal gap-2 min-w-0 flex-1">
								{!status && (
									<Circle className="fill-accent w-2.5 h-2.5 flex-shrink-0" />
								)}
								{isPinned && (
									<Flag className="w-4 h-4 fill-negative flex-shrink-0" />
								)}
								{messageIcon}
								<Tooltip
									label={info.getValue() as string}
									className={cn(isMobile && 'opacity-0')}
								>
									<p
										className={cn(
											'w-auto text-sm pl-0 text-text-main dark:text-text-table-dark truncate',
											!status && 'font-bold'
										)}
									>
										{info.getValue() as string}
									</p>
								</Tooltip>
							</div>
						</div>
					)
				},
			},
			{
				accessorKey: 'from',
				enableResizing: true,
				minSize: 100,
				size: isMobile ? 100 : 150,
				header: ({ column }: { column: Column<InboxTableEntry, unknown> }) =>
					renderSortingHeader('From', column, 'pl-4'),
				cell: (info) => {
					const status = info?.row?.original?.itemStatus
					return (
						<span className={cn('text-sm ', !status && 'font-bold')}>
							{info.getValue() as string}
						</span>
					)
				},
			},
			{
				accessorKey: 'createdAt',
				enableResizing: true,
				minSize: 100,
				size: isMobile ? 100 : 150,
				header: ({ column }: { column: Column<InboxTableEntry, unknown> }) =>
					renderSortingHeader('Received on', column, 'pl-4'),
				cell: (info) => {
					const status = info?.row?.original?.itemStatus
					const val = info.getValue()
					return (
						<span className={cn('text-sm ', !status && 'font-bold')}>
							{val ? formatDateTime(String(val)) : '-'}
						</span>
					)
				},
			},
			{
				accessorKey: 'options',
				enableResizing: false,
				size: 50,
				maxSize: 50,
				minSize: 25,
				header: () => (
					<div className="flex items-center justify-end mr-2 gap-2 overflow-hidden h-full">
						<IconButton
							variant="transparent"
							className="max-w-[20px] max-h-[20px] w-5 h-5 p-0 group hover:bg-transparent"
							tooltipPosition="left"
							icon={
								<ResetColumnsSize className="fill-text-table dark:fill-text-table-dark w-full h-full group-hover:fill-accent-hover" />
							}
							onClick={() => table.resetColumnSizing()}
							tooltip={'Reset columns sizes'}
						/>
						{!!columnFilters?.length && (
							<IconButton
								variant="transparent"
								className="max-w-[20px] max-h-[20px] w-5 h-5 p-0 group hover:bg-transparent"
								tooltipPosition="left"
								icon={
									<FilterAltOff className="fill-text-table dark:fill-text-table-dark w-full h-full group-hover:fill-accent-hover" />
								}
								onClick={() => onChangeFilters([])}
								tooltip={'Clean filters'}
							/>
						)}
					</div>
				),
				cell: ({ row }) => {
					buttonsUpdateTrigger.current

					const id = row?.original?.id
					const isRead = row?.original?.itemStatus
					const isPinned = row?.original?.itemPinned
					const isLoadingRead = isItemLoadingRead(id)
					const isLoadingFavorite = isItemLoadingFavorite(id)

					return (
						<div className="items-center h-full justify-end gap-2 w-fit md:ml-auto px-4 bg-inherit md:absolute right-0 group-hover:lg:left-custom-shadow hidden md:flex opacity-0 lg:group-hover:opacity-100 transition-all duration-150 ease-in-out">
							<IconButton
								className="w-8 h-8 rounded-lg"
								variant={'secondary'}
								disabled={isLoadingRead}
								icon={
									isRead ? (
										<Markunread className="w-4" />
									) : (
										<Drafts className="w-4" />
									)
								}
								tooltip={isRead ? 'Mark as unread' : 'Mark as read'}
								onClick={async () => {
									if (id) {
										manageLoadingState('add', 'read', id)
										try {
											await toggleReadStatus(
												{ id, value: !isRead },
												{ onSuccess: onRefetch }
											)
										} finally {
											manageLoadingState('remove', 'read', id)
										}
									}
								}}
							/>

							<IconButton
								className="w-8 h-8 rounded-lg"
								variant={'secondary'}
								disabled={isLoadingFavorite}
								icon={
									isPinned ? (
										<Flag className="w-4 fill-negative" />
									) : (
										<FlagOutlined className="w-4" />
									)
								}
								tooltip={
									isPinned ? 'Remove from important' : 'Mark to important'
								}
								onClick={async () => {
									if (id) {
										manageLoadingState('add', 'favorite', id)
										try {
											await togglePinnedStatus({
												id,
												value: !isPinned,
											})
										} finally {
											manageLoadingState('remove', 'favorite', id)
										}
									}
								}}
							/>

							<IconButton
								className="w-8 h-8 rounded-lg"
								variant="secondary"
								icon={<Delete className="w-4" />}
								tooltip="Delete"
								onClick={() => onDeleteItem(row?.original?.id)}
							/>
						</div>
					)
				},
			},
		]

		if (isMobile) {
			return base.filter(
				(
					columnDef
				): columnDef is AccessorKeyColumnDef<InboxTableEntry, any> => {
					return (
						'accessorKey' in columnDef &&
						['id', 'name', 'itemStatus', 'itemCategory'].includes(
							columnDef.accessorKey
						)
					)
				}
			)
		}

		return base
	}, [checkedItems, isMobile, isWide, maxAvailableNameSize, columnFilters])

	const table = useReactTable({
		data: tableData,
		columns,
		columnResizeMode: 'onChange',
		getCoreRowModel: getCoreRowModel(),
	})

	const buttonsUpdateTrigger = useRef(0)

	const forceButtonsUpdate = useCallback(() => {
		buttonsUpdateTrigger.current += 1
	}, [])

	const manageLoadingState = useCallback(
		(action: 'add' | 'remove', type: 'favorite' | 'read', id: string) => {
			const targetSet =
				type === 'read' ? loadingReadItems : loadingFavoriteItems

			if (action === 'add') {
				targetSet.current.add(id)
			} else {
				targetSet.current.delete(id)
			}

			forceButtonsUpdate()
		},
		[forceButtonsUpdate]
	)

	const isItemLoadingRead = useCallback(
		(id: string) => loadingReadItems.current.has(id),
		[]
	)
	const isItemLoadingFavorite = useCallback(
		(id: string) => loadingFavoriteItems.current.has(id),
		[]
	)

	return { table }
}
